'use client';
import { useCallback, useRef, useEffect } from 'react';

export interface UseDebounceOptions {
    withCancel?: boolean;
}

/**
 * useDebouncedCallback (Arrow Function Version)
 * Returns a debounced version of a callback function.
 *
 * @param callback - Function to debounce
 * @param delay - Debounce delay in ms (default: 300ms)
 * @param options - Optional. Set withCancel: true to get [debouncedFn, cancel].
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface UseDebounce {
    <T extends (...args: any[]) => void>(
        callback: T,
        delay: number,
        options: UseDebounceOptions & { withCancel: true }
    ): [(...args: Parameters<T>) => void, () => void];
    <T extends (...args: any[]) => void>(
        callback: T,
        delay?: number,
        options?: UseDebounceOptions
    ): (...args: Parameters<T>) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useDebounce = (<T extends (...args: any[]) => void>(
    callback: T,
    delay = 300,
    options?: UseDebounceOptions
): ((...args: Parameters<T>) => void) | [(...args: Parameters<T>) => void, () => void] => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const callbackRef = useRef(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const debouncedFn = useCallback(
        (...args: Parameters<T>) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;
                callbackRef.current(...args);
            }, delay);
        },
        [delay]
    );

    useEffect(() => {
        return () => cancel();
    }, [cancel]);

    if (options?.withCancel) {
        return [debouncedFn, cancel];
    }
    return debouncedFn;
}) as UseDebounce;
